import React, { useState, useEffect, useRef } from 'react';
import { LayoutService } from '../../services/layoutService';
import { DashboardLayout } from '../../types/dashboard';
import { TestTile } from './TestTile';
import { HealthCalendarTile } from './HealthCalendarTile';
import { DailyMetricsTile } from './DailyMetricsTile';
import { QuickActionsTile } from './QuickActionsTile';
import { DraggableTile } from './DraggableTile';

interface TileGridProps {
  onNavigate?: (view: string) => void;
}

export const TileGrid: React.FC<TileGridProps> = ({ onNavigate }) => {
  const [layout, setLayout] = useState<DashboardLayout | null>(null);
  const [draggedTileId, setDraggedTileId] = useState<string | null>(null);
  const [isMovementEnabled, setIsMovementEnabled] = useState(false);
  const [showGrid, setShowGrid] = useState(false);
  const [dropPreview, setDropPreview] = useState<{ x: number; y: number; isValid: boolean } | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Create initial dashboard layout with real tiles
    const dashboardTiles = ['calendar-summary', 'daily-metrics', 'quick-actions'];
    const defaultLayout = LayoutService.getDefaultLayout(dashboardTiles);
    setLayout(defaultLayout);
  }, []);

  // Drag handlers
  const handleDragStart = (tileId: string) => {
    setDraggedTileId(tileId);
    setShowGrid(true);
  };

  const handleDragMove = (clientX: number, clientY: number) => {
    if (!gridRef.current || !layout || !draggedTileId) return;
    
    const rect = gridRef.current.getBoundingClientRect();
    const draggedTile = layout.tiles.find(t => t.id === draggedTileId);
    const tileWidth = draggedTile ? draggedTile.width : 2;
    
    const gridPos = LayoutService.findNearestGridPosition(clientX, clientY, rect, layout.gridCols, tileWidth);
    
    // Check if this position is valid (no collisions)
    const isValid = draggedTile ? LayoutService.isValidDropPosition(
      layout,
      draggedTileId,
      gridPos.x,
      gridPos.y,
      draggedTile.width,
      draggedTile.height
    ) : false;
    
    setDropPreview({ ...gridPos, isValid });
  };

  const handleDragEnd = (tileId: string, clientX: number, clientY: number) => {
    if (!gridRef.current || !layout) return;
    
    const rect = gridRef.current.getBoundingClientRect();
    const draggedTile = layout.tiles.find(t => t.id === tileId);
    const tileWidth = draggedTile ? draggedTile.width : 2;
    
    const newPos = LayoutService.findNearestGridPosition(clientX, clientY, rect, layout.gridCols, tileWidth);
    
    // Try to place tile at new position (prevention-based)
    const newLayout = LayoutService.tryPlaceTile(layout, tileId, newPos.x, newPos.y);
    
    if (newLayout) {
      // Valid drop - update layout
      setLayout(newLayout);
    }
    // If newLayout is null, tile will snap back to original position (no layout change)
    
    setDraggedTileId(null);
    setShowGrid(false);
    setDropPreview(null);
  };

  if (!layout) {
    return <div>Loading grid...</div>;
  }

  const gridStyle: React.CSSProperties = {
    position: 'relative',
    display: 'grid',
    gridTemplateColumns: `repeat(${layout.gridCols}, 1fr)`,
    gridTemplateRows: LayoutService.getGridTemplateRows(layout),
    gap: '12px',
    padding: '20px',
    backgroundColor: 'var(--bg-secondary)',
    minHeight: '600px',
    border: '1px solid var(--border-color)',
    borderRadius: 'var(--radius-large)'
  };

  return (
    <div>
      {/* Movement Toggle - iOS style */}
      <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
          <div
            onClick={() => setIsMovementEnabled(!isMovementEnabled)}
            style={{
              width: '48px',
              height: '28px',
              borderRadius: '14px',
              backgroundColor: isMovementEnabled ? 'var(--accent-blue)' : 'var(--border-color)',
              position: 'relative',
              transition: 'all 0.3s ease',
              border: '2px solid transparent',
              boxSizing: 'border-box'
            }}
          >
            <div
              style={{
                width: '24px',
                height: '24px',
                borderRadius: '12px',
                backgroundColor: 'white',
                position: 'absolute',
                top: '2px',
                left: isMovementEnabled ? '22px' : '2px',
                transition: 'all 0.3s ease',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
              }}
            />
          </div>
          <span style={{ 
            fontSize: '14px', 
            fontWeight: '500',
            color: 'var(--text-primary)' 
          }}>
            Customize dashboard
          </span>
        </label>
        {isMovementEnabled && (
          <span style={{ 
            fontSize: '12px', 
            color: 'var(--text-secondary)',
            fontStyle: 'italic'
          }}>
            Drag tiles to rearrange
          </span>
        )}
      </div>
      
      <div ref={gridRef} style={gridStyle}>
        {/* Grid overlay for visual feedback during drag */}
        {showGrid && (
          <div 
            className="grid-overlay"
            style={{ 
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'grid',
              gridTemplateColumns: `repeat(${layout.gridCols}, 1fr)`,
              gridTemplateRows: LayoutService.getGridTemplateRows(layout),
              gap: '12px',
              pointerEvents: 'none',
              zIndex: 500
            }}
          >
            {Array.from({ length: layout.gridCols * 6 }).map((_, index) => {
              const x = index % layout.gridCols;
              const y = Math.floor(index / layout.gridCols);
              
              let backgroundColor = 'rgba(0, 0, 0, 0.05)';
              let borderColor = 'rgba(0, 0, 0, 0.2)';
              
              // Check if this cell is part of the tile footprint being dragged
              if (dropPreview && draggedTileId) {
                const draggedTile = layout.tiles.find(t => t.id === draggedTileId);
                if (draggedTile) {
                  const isInFootprint = (
                    x >= dropPreview.x && 
                    x < dropPreview.x + draggedTile.width &&
                    y >= dropPreview.y && 
                    y < dropPreview.y + draggedTile.height
                  );
                  
                  if (isInFootprint) {
                    if (dropPreview.isValid) {
                      // Valid drop - green for entire footprint
                      backgroundColor = 'rgba(16, 185, 129, 0.3)';
                      borderColor = 'rgba(16, 185, 129, 0.6)';
                    } else {
                      // Invalid drop - red for entire footprint
                      backgroundColor = 'rgba(239, 68, 68, 0.3)';
                      borderColor = 'rgba(239, 68, 68, 0.6)';
                    }
                  }
                }
              }
              
              return (
                <div
                  key={index}
                  style={{
                    backgroundColor,
                    border: `1px dashed ${borderColor}`,
                    borderRadius: '4px'
                  }}
                />
              );
            })}
          </div>
        )}
        
        {layout.tiles.map((tilePosition) => (
          <DraggableTile
            key={tilePosition.id}
            position={tilePosition}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragMove={handleDragMove}
            isBeingDragged={draggedTileId === tilePosition.id}
            isMovementEnabled={isMovementEnabled}
          >
            {tilePosition.id === 'calendar-summary' ? (
              <HealthCalendarTile />
            ) : tilePosition.id === 'daily-metrics' ? (
              <DailyMetricsTile onExpand={() => onNavigate?.('health-metrics')} />
            ) : tilePosition.id === 'quick-actions' ? (
              <QuickActionsTile onNavigate={onNavigate} />
            ) : (
              <TestTile
                id={tilePosition.id}
                title="Unknown Tile"
                color="var(--accent-gray)"
              />
            )}
          </DraggableTile>
        ))}
      </div>
    </div>
  );
};